import { useState } from "react";
import { ApiError, account, auth, type User } from "../../../api/client";
import { UNITS } from "../../../lib/units";

export function SettingsView({ user, onUserChange, onDeleted }: { user: User; onUserChange: (u: User) => void; onDeleted: () => void }) {
  const [email, setEmail] = useState(user.email ?? "");
  const [first, setFirst] = useState(user.first_name);
  const [last, setLast] = useState(user.last_name);
  const [showName, setShowName] = useState(user.show_name_on_shared);
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [current, setCurrent] = useState("");
  const [status, setStatus] = useState("");
  const [pwStatus, setPwStatus] = useState("");
  const [deletePw, setDeletePw] = useState("");
  const [delStatus, setDelStatus] = useState("");

  async function save(unitId = user.default_unit_id) {
    try {
      const u = await account.update({ email: email.trim() || null, first_name: first, last_name: last, default_unit_id: unitId, show_name_on_shared: showName });
      onUserChange(u);
      setStatus("Saved.");
    } catch (e) {
      setStatus(e instanceof ApiError ? e.message : "Could not save.");
    }
  }

  async function changePassword() {
    if (p1 !== p2) return setPwStatus("Passwords don't match.");
    if (p1.length < 8) return setPwStatus("Use at least 8 characters.");
    try {
      await auth.change(current, p1);
      setPwStatus("Password updated.");
      setP1("");
      setP2("");
      setCurrent("");
    } catch (e) {
      setPwStatus(e instanceof ApiError ? e.message : "Could not update the password.");
    }
  }

  async function deleteAccount() {
    if (!window.confirm("Delete your account and all of your gear? This cannot be undone.")) return;
    try {
      await account.remove(deletePw);
      onDeleted();
    } catch (e) {
      setDelStatus(e instanceof ApiError ? e.message : "Could not delete the account.");
    }
  }

  return (
    <div className="SettingsView shown">
      <div className="content">
        <div className="username">Username: {user.username}</div>
        {user.needs_email_fix && <div className="box" style={{ borderColor: "#b72c0e" }}>Please add an email address so you can recover your password.</div>}
        <div className="box">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <div>Email:</div>
            <div>
              <input id="emailInput" type="text" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>First Name:</div>
            <div>
              <input id="firstNameInput" type="text" aria-label="First name" value={first} onChange={(e) => setFirst(e.target.value)} />
            </div>
            <div>Last Name:</div>
            <div>
              <input id="lastNameInput" type="text" aria-label="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
            </div>
            <div>
              <label>
                <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} /> Show my name on shared lists
              </label>
            </div>
            <div>
              <a className="button" href="#save" onClick={(e) => (e.preventDefault(), save())}>
                save
              </a>{" "}
              <span role="status">{status}</span>
            </div>
          </form>
        </div>
        <div className="box">
          Default Unit:{" "}
          <select aria-label="Default unit" value={user.default_unit_id} onChange={(e) => save(Number(e.target.value))}>
            {UNITS.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="box">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              changePassword();
            }}
          >
            <div>Current Password:</div>
            <div>
              <input type="password" aria-label="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
            <div>New Password:</div>
            <div>
              <input type="password" id="password1Input" aria-label="New password" value={p1} onChange={(e) => setP1(e.target.value)} />
            </div>
            <div>Retype Password:</div>
            <div>
              <input type="password" id="password2Input" aria-label="Retype password" value={p2} onChange={(e) => setP2(e.target.value)} />
            </div>
            <div>
              <a className="button" href="#savepw" onClick={(e) => (e.preventDefault(), changePassword())}>
                save
              </a>{" "}
              <span role="status">{pwStatus}</span>
            </div>
          </form>
        </div>
        <div className="box">
          <div>Delete account (type your password):</div>
          <div>
            <input type="password" aria-label="Password to delete account" value={deletePw} onChange={(e) => setDeletePw(e.target.value)} />
          </div>
          <div>
            <a className="button" href="#delete" onClick={(e) => (e.preventDefault(), deleteAccount())}>
              delete account
            </a>{" "}
            <span role="status">{delStatus}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
